<script>
  import { useUrlParams } from "@stores/urlStore.js";
  import { writable } from "svelte/store";
  import { tick } from "svelte";
  import { fetchWithSession } from "../../../../../lib/util/fetchWithSession";
  import {
    SvelteFlow,
    MarkerType,
    MiniMap,
    ConnectionMode,
    useSvelteFlow,
  } from "@xyflow/svelte";

  import "@xyflow/svelte/dist/style.css";
  import { dagre } from "dagre-d3";
  import * as d3 from "d3";
  import * as dagreD3 from "dagre-d3";
  import GraphGrouper from "lib/components/GraphGrouper.svelte";
  import CustomNode from "./CustomNode.svelte";
  import CustomEdge from "./PercentageEdge.svelte";
  import CustomGroupNode from "./CustomGroupNode.svelte";
  import WorkflowExplorerDropdownMenu from "./workflowExplorerDropdownMenu.svelte";
  import { onMount } from "svelte";

  export let nodesData = [];
  export let edgesData = [];
  export let variants = [];
  export let subgraphs = [];
  export let width = "500px";
  export let height = "500px";
  export let selectedNode = "";
  export let selectedCluster = "";
  export let fullNodeInfo = false;
  export let updateZoomLevel;
  export let selectedNodes = [];
  export let expandedNodes = [];
  export let triggerUpdate;
  export let executionFrequency = 1;
  export let minFrequency = 0;
  export let nvariants = 1;
  export let seeMinimap = false;
  export let paxbotVisible = false;
  export let paxbotMessage = "";
  export let paxbotTitle = "";
  export let activitiesToShow = [];

  export async function downloadSVG(selectedProcesses) {
    const flowContainer = document.querySelector(".svelte-flow");
    if (!flowContainer) return;

    const svgNS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(svgNS, "svg");

    // Get viewport transform
    const viewport = flowContainer.querySelector(".svelte-flow__viewport");
    const transform = viewport ? viewport.style.transform : "";

    const translateMatch = transform.match(/translate\(([^)]+)\)/);
    const scaleMatch = transform.match(/scale\(([^)]+)\)/);

    const [translateX = 0, translateY = 0] = translateMatch
      ? translateMatch[1].split(",").map((v) => parseFloat(v))
      : [0, 0];
    const scale = scaleMatch ? parseFloat(scaleMatch[1]) : 1;

    // Calculate SVG dimensions
    const nodes = flowContainer.querySelectorAll(".svelte-flow__node");
    const edgeLabels = flowContainer.querySelectorAll(".edge-label");
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    // Include calculation-centric nodes
    nodes.forEach((node) => {
      const transform = node.style.transform;
      const match = transform.match(/translate\(([^)]+)\)/);
      if (match) {
        const [x, y] = match[1].split(",").map((v) => parseFloat(v));

        const nodeRect = node.getBoundingClientRect();
        const actualWidth = nodeRect.width + 4;
        const actualHeight = 60 + 4;

        const centeredX = x - actualWidth / 2 + actualWidth * 1.2;

        minX = Math.min(minX, centeredX);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, centeredX + actualWidth);
        maxY = Math.max(maxY, y + actualHeight);
      }
    });

    // Include edge labels in the calculation
    edgeLabels.forEach((label) => {
      const transform = label.style.transform;
      const match = transform.match(/translate\(([^)]+)\)/);
      if (match) {
        const [x, y] = match[1]
          .split(",")
          .map((v) => parseFloat(v.replace("px", "")));
        const labelRect = label.getBoundingClientRect();
        const labelWidth = labelRect.width;

        minX = Math.min(minX, x - labelWidth / 2);
        minY = Math.min(minY, y - 20);
        maxX = Math.max(maxX, x + labelWidth / 2);
        maxY = Math.max(maxY, y + 60 + 20);
      }
    });

    const padding = 100;
    const svgWidth =
      maxX - minX + padding * 2 > 1000 ? maxX - minX + padding * 2 : 1000;
    const svgHeight = maxY - minY + padding * 2;

    svg.setAttribute("width", svgWidth);
    svg.setAttribute("height", svgHeight);
    svg.setAttribute("viewBox", `0 0 ${svgWidth} ${svgHeight}`);

    // white background
    const bg = document.createElementNS(svgNS, "rect");
    bg.setAttribute("width", "100%");
    bg.setAttribute("height", "100%");
    bg.setAttribute("fill", "white");
    svg.appendChild(bg);

    // Get styles
    const targetClasses = [
      ".svelte-flow",
      ".custom-node",
      ".container",
      ".label-node",
      ".edge-label",
      ".start-end-node",
      ".more_filled_icon",
      "button",
      ".svelte-flow__edges",
      ".svelte-flow__edge-path",
    ];
    const extractedStyles = extractClassStyles(targetClasses);

    // Add additional styles
    const style = document.createElementNS(svgNS, "style");
    style.textContent = `
      ${extractedStyles}
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    * {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      letter-spacing: 0.16px;
      font-size: 0.875rem;
      font-weight:400;
      line-height: 1.28572;
      line-height: 1;
      box-sizing: border-box !important;
    }
      foreignObject {
        overflow: visible;
      }
      .custom-node {
         padding: 11px 20px !important;

        }
    `;
    svg.appendChild(style);

    // Convert images to base64
    const imagePromises = [];
    nodes.forEach((node) => {
      const images = node.querySelectorAll("img");
      images.forEach((img) => {
        if (img.src && !img.src.startsWith("data:")) {
        const promise = fetch(img.src)
            .then((response) => response?.ok ? response.blob() : null)
            .then(
              (blob) =>
                blob && new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onload = () => {
                    img.src = reader.result;
                    resolve();
                  };
                  reader.readAsDataURL(blob);
                }),
            )
            .catch(() => console.warn("Could not convert image:", img.src));

          imagePromises.push(promise);
        }
      });
    });

    await Promise.all(imagePromises);

    // print main group with offset
    const mainGroup = document.createElementNS(svgNS, "g");
    mainGroup.setAttribute(
      "transform",
      `translate(${padding - minX}, ${padding - minY})`,
    );

    // copy markers
    const defs = document.createElementNS(svgNS, "defs");
    const markers = flowContainer.querySelectorAll(
      ".svelte-flow__marker marker",
    );
    markers.forEach((marker) => {
      defs.appendChild(marker.cloneNode(true));
    });
    svg.appendChild(defs);

    // copy nodes
    nodes.forEach((node) => {
      const transform = node.style.transform;
      const match = transform.match(/translate\(([^)]+)\)/);
      if (!match) return;

      const [x, y] = match[1].split(",").map((v) => parseFloat(v));

      const nodeRect = node.getBoundingClientRect();
      const actualWidth = nodeRect.width;
      const actualHeight = nodeRect.height;

      const foreignObject = document.createElementNS(svgNS, "foreignObject");
      foreignObject.setAttribute("x", x);
      foreignObject.setAttribute("y", y);
      foreignObject.setAttribute("width", actualWidth);
      foreignObject.setAttribute("height", actualHeight);

      const nodeContent = node.querySelector(".container");
      if (nodeContent) {
        const contentClone = nodeContent.cloneNode(true);
        foreignObject.appendChild(contentClone);
        mainGroup.appendChild(foreignObject);
      }
    });

    // Create SVG group for edges with class (rendered on top)
    const edgesGroup = document.createElementNS(svgNS, "g");
    edgesGroup.setAttribute("class", "svelte-flow__edges");

    // Copy edges paths
    const edges = flowContainer.querySelectorAll(
      ".svelte-flow__edge path.svelte-flow__edge-path",
    );
    edges.forEach((edge) => {
      const pathClone = edge.cloneNode(true);
      edgesGroup.appendChild(pathClone);
    });

    mainGroup.appendChild(edgesGroup);

    // Copy edge labels
    edgeLabels.forEach((label) => {
      const transform = label.style.transform;
      const match = transform.match(/translate\(([^)]+)\)/);
      if (match) {
        const [x, y] = match[1]
          .split(",")
          .map((v) => parseFloat(v.replace("px", "")));

        const labelRect = label.getBoundingClientRect();
        const labelWidth = labelRect.width;

        const foreignObject = document.createElementNS(svgNS, "foreignObject");
        foreignObject.setAttribute("x", x + 50);
        foreignObject.setAttribute("y", y + 50);
        foreignObject.setAttribute("width", labelWidth);
        foreignObject.setAttribute("height", "60");

        const labelClone = label.cloneNode(true);

        foreignObject.appendChild(labelClone);
        mainGroup.appendChild(foreignObject);
      }
    });

    svg.appendChild(mainGroup);

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);

    const blob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);

    let fileName = "";
    selectedProcesses.forEach((process) => {
      fileName += process.name + "-";
    });
    fileName += "process_explorer.svg";
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();

    URL.revokeObjectURL(url);
  }

  const urlParams = useUrlParams();
  const { getZoom, viewport } = useSvelteFlow();
  const nodes = writable([]);
  const edges = writable([]);

  let zoomLevel = writable((getZoom() * 100).toFixed(0));
  let currentParentNode;
  let isActivityNode = false;
  let contextMenuOpen = false;
  let menuPosition = { x: 0, y: 0 };
  let subprocessMenuOpen = false;
  let isLoadingGraph = false;
  let openWindowSequenceNodes = [];

  const nodeTypes = {
    custom: CustomNode,
    group: CustomGroupNode,
  };

  const edgeTypes = {
    customEdg: CustomEdge,
  };

  $: ({ analysisid } = $urlParams);
  $: activitiesToShow = $nodes;

  $: {
    if ($viewport.zoom != zoomLevel) {
      updateZoomLevel();
    }
  }

  // $: setData(nodesData, edgesData, fullNodeInfo, minFrequency, nvariants);

  $: {
    {
      nodesData,
        edgesData,
        fullNodeInfo,
        minFrequency,
        nvariants,
        openWindowSequenceNodes;
    }
    resetData().then(() => setData());
  }

  function AIExplanation() {
    let unifiedtitle = nodesData.filter((node) => node.id === selectedNode)[0]
      .unifiedtitle;
    fetchWithSession(
      `/api/analysis/apps/getWindowAnnotation?analysisid=${analysisid}&unifiedtitle=${unifiedtitle}`,
      {
        method: "GET",
      },
    )
      .then((response) => response.json())
      .then((data) => {
        const { annotation, unifiedtitle } = data;
        paxbotMessage = JSON.parse(annotation);
        paxbotVisible = true;
        paxbotTitle = "AI Explanation for " + unifiedtitle;
      });
  }

  function openNodeMenu(evt, id) {
    evt.preventDefault();
    evt.stopPropagation();
    if (contextMenuOpen) return (contextMenuOpen = !contextMenuOpen);
    selectedNode = id;
    let currentnode = nodesData.filter((node) => node.id === id)[0];
    if (!currentnode.isSubProcess) {
      currentParentNode =
        evt.target.classList.length > 1 ? evt.target.classList[1] : false;
      isActivityNode = true;
      selectedNode = id;
    } else {
      currentParentNode = false;
      isActivityNode = false;
      selectedCluster = id;
    }
    const iconBoundingRect = evt.target.getBoundingClientRect();
    menuPosition = {
      x: iconBoundingRect.left + window.scrollX,
      y: iconBoundingRect.top + window.scrollY,
    };
    contextMenuOpen = true;
  }

  function calculateNodeWidth(label, hasButton = true, windowSequence = []) {
    const iconWidth = 35;
    const buttonWidth = hasButton ? 30 : 0;
    const padding = 32;
    const border = 4;
    const gap = 8;

    const segments = label.split("<br>");
    let maxWidth = 0;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = "14px Arial";

    segments.forEach((segment) => {
      const textWidth = ctx.measureText(segment).width;
      if (textWidth > maxWidth) {
        maxWidth = textWidth;
      }
    });
    (windowSequence || []).forEach((item, index) => {
      const sequenceLabel = `${index + 1}. ${item.app ? `${item.app}: ` : ""}${item.windowTitle || ""} [${item.count || 0}]`;
      maxWidth = Math.max(maxWidth, ctx.measureText(sequenceLabel).width);
    });

    const totalWidth =
      iconWidth +
      maxWidth +
      buttonWidth +
      padding +
      border +
      gap * (hasButton ? 2 : 1);

    return totalWidth;
  }

  function calculateNodeHeight(label, windowSequence = []) {
    const baseHeight = 62;
    const lineHeight = 6;

    const lines = label.split("<br>").length;
    const sequenceHeight = (windowSequence || []).length
      ? 16 + (windowSequence || []).length * 28
      : 0;
    const totalHeight = baseHeight + (lines - 1) * lineHeight + sequenceHeight;
    return totalHeight;
  }

  function filterGraphByFrequency(nodesIn, edgesIn, minFreq, nvariants) {
    const nodes = [...nodesIn];
    let allEdges = [...edgesIn];

    // STEP 0: Filter edges self-loops
    allEdges = allEdges.filter((e) => e.from !== e.to);

    // STEP 1: Sort edges by value (smallest first)
    allEdges = allEdges.sort((a, b) => a.value - b.value);

    // STEP 2: Calculate variant edges
    const variantEdgesSet = new Set();
    for (let i = 0; i < Math.min(variants.length, nvariants); i++) {
      const variant = variants[i];
      for (let j = 0; j < variant.length - 1; j++) {
        const from = variant[j];
        const to = variant[j + 1];
        variantEdgesSet.add(`${from}-${to}`);
      }
    }

    const variantEdges = allEdges.filter((e) =>
      variantEdgesSet.has(`${e.from}-${e.to}`),
    );

    // STEP 3: Calculate absolute minFrequency
    const executionFrequencyDecimal = executionFrequency / 100;
    const minFrequency = executionFrequencyDecimal * minFreq;

    // STEP 4: Create equivalent NetworkX graph
    let G = [...variantEdges];

    // STEP 5: Find Start and End nodes (multiple)
    const startNodes = [];
    const endNodes = [];
    const nodeIds = new Set();

    // Collect all nodes from the graph G
    G.forEach((edge) => {
      nodeIds.add(edge.from);
      nodeIds.add(edge.to);
    });

    // Filter Start and End nodes
    nodeIds.forEach((nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;

      if (
        nodeId.includes("Start") ||
        (node.label && node.label.includes("Start"))
      ) {
        startNodes.push(nodeId);
      }
      if (
        nodeId.includes("End") ||
        (node.label && node.label.includes("End"))
      ) {
        endNodes.push(nodeId);
      }
    });

    if (startNodes.length === 0 || endNodes.length === 0) {
      console.warn("No Start/End nodes found");
      return { nodes: nodesIn.slice(), edges: edgesIn.slice() };
    }

    // Function to check connectivity using BFS (equivalent to local_node_connectivity)
    function localNodeConnectivity(fromId, toId, edgeList) {
      if (fromId === toId) return true;

      const visited = new Set();
      const queue = [fromId];
      const adjacency = new Map();

      // Build adjacency map
      const allNodeIds = new Set();
      edgeList.forEach((e) => {
        allNodeIds.add(e.from);
        allNodeIds.add(e.to);
      });

      allNodeIds.forEach((nodeId) => adjacency.set(nodeId, []));
      edgeList.forEach((e) => {
        adjacency.get(e.from).push(e.to);
      });

      while (queue.length > 0) {
        const current = queue.shift();
        if (current === toId) return true;
        if (visited.has(current)) continue;

        visited.add(current);
        const neighbors = adjacency.get(current) || [];
        neighbors.forEach((neighbor) => {
          if (!visited.has(neighbor)) {
            queue.push(neighbor);
          }
        });
      }

      return false;
    }

    let GTemp = [...G];

    for (const edge of variantEdges) {
      if (edge.value < minFrequency) {
        // Try to remove the edge
        const tempEdgesWithoutCurrent = GTemp.filter(
          (e) => !(e.from === edge.from && e.to === edge.to),
        );

        // Check connectivity for all start-end pairs
        let shouldKeepEdge = false;
        for (const startNode of startNodes) {
          for (const endNode of endNodes) {
            if (
              !localNodeConnectivity(
                startNode,
                endNode,
                tempEdgesWithoutCurrent,
              )
            ) {
              shouldKeepEdge = true;
              break;
            }
          }
          if (shouldKeepEdge) break;
        }

        if (!shouldKeepEdge) {
          // If connectivity is not broken, remove the edge
          GTemp = tempEdgesWithoutCurrent;
        }
      }
    }

    let newEdges = [...variantEdges];

    // Get all nodes from GTemp
    const tempNodeIds = new Set();
    GTemp.forEach((edge) => {
      tempNodeIds.add(edge.from);
      tempNodeIds.add(edge.to);
    });

    for (const nodeId of tempNodeIds) {
      if (!nodeId.includes("Start") && !nodeId.includes("End")) {
        // Verify connectivity with any start
        const cstart = startNodes.some((startNode) =>
          localNodeConnectivity(startNode, nodeId, GTemp),
        );
        // Verify connectivity with any end
        const cend = endNodes.some((endNode) =>
          localNodeConnectivity(nodeId, endNode, GTemp),
        );

        if (!cstart && !cend) {
          // Remove from the original graph G and from newEdges
          G = G.filter((e) => e.from !== nodeId && e.to !== nodeId);
          newEdges = newEdges.filter(
            (e) => e.from !== nodeId && e.to !== nodeId,
          );
        }
      }
    }

    for (const edge of [...newEdges]) {
      // Check if the edge still exists in G
      const edgeStillExists = G.some(
        (e) => e.from === edge.from && e.to === edge.to,
      );

      if (edgeStillExists && edge.value < minFrequency) {
        // Create a temporary copy without this edge
        const tempG = G.filter(
          (e) => !(e.from === edge.from && e.to === edge.to),
        );

        // Check local connectivity with all start/end nodes
        let canSafelyRemove = true;

        // Verify that all starts can reach the target node
        for (const startNode of startNodes) {
          if (!localNodeConnectivity(startNode, edge.to, tempG)) {
            canSafelyRemove = false;
            break;
          }
        }

        // Verify that the source node can reach all ends
        if (canSafelyRemove) {
          for (const endNode of endNodes) {
            if (!localNodeConnectivity(edge.from, endNode, tempG)) {
              canSafelyRemove = false;
              break;
            }
          }
        }

        if (canSafelyRemove) {
          G = tempG;
        }
      }
    }

    // STEP 6: Filter final nodes that have at least one connection in G
    const connectedNodeIds = new Set();
    G.forEach((edge) => {
      connectedNodeIds.add(edge.from);
      connectedNodeIds.add(edge.to);
    });

    // Make sure Start and End nodes are included if G is not empty
    if (G.length > 0) {
      startNodes.forEach((nodeId) => connectedNodeIds.add(nodeId));
      endNodes.forEach((nodeId) => connectedNodeIds.add(nodeId));
    }

    const filteredNodes = nodes.filter((n) => connectedNodeIds.has(n.id));

    return { nodes: filteredNodes, edges: G };
  }

  function calculateNodePositions(
    nodes,
    edges,
    fullNodeInfo,
    minFrequency,
    nvariants,
  ) {
    const g = new dagreD3.graphlib.Graph({ compound: true }).setGraph({
      rankdir: "TB",
      nodesep: 250,
      ranksep: 125,
      transitions: true,
    });
    g.setDefaultEdgeLabel(() => ({}));

    g.graph().transition = function transition(selection) {
      return selection.transition().duration(1000);
    };

    const relevantNodes = new Set();

    //  Add filter by frequency
    let { nodes: filteredNodes, edges: filteredEdges } = filterGraphByFrequency(
      nodes,
      edges,
      minFrequency,
      nvariants,
    );
    nodes = filteredNodes;
    edges = filteredEdges;

    // Make sure Start/End nodes are included if G is not empty
    const startNode = nodes.find(
      (n) =>
        n.label === "Start" ||
        (n.label && n.label.startsWith("Start")) ||
        (n.id && n.id.includes("Start")),
    );
    const endNode = nodes.find(
      (n) =>
        n.label === "End" ||
        (n.label && n.label.startsWith("End")) ||
        (n.id && n.id.includes("End")),
    );

    if (startNode) relevantNodes.add(startNode.id);
    if (endNode) relevantNodes.add(endNode.id);

    g.setNode("mainprocess_cluster", {
      label: "",
      labelType: "html",
      style: "fill:none;",
    });

    if (subgraphs) {
      subgraphs.forEach((node) => {
        g.setNode(node.clusterid, {
          label: node.label,
          labelType: "html",
          clusterLabelPos: "top",
          style: `fill:${node.color};`,
          rx: 8,
          ry: 8,
        });
        g.setParent(node.clusterid, "mainprocess_cluster");
      });
    }

    // Add parents of relevant nodes
    nodes.forEach((node) => {
      if (node.parentNode) {
        relevantNodes.add(node.parentNode);
      }
    });

    // Add filtered nodes to dagre
    nodes.forEach((node) => {
      let labelContent = fullNodeInfo
        ? node.label
        : node.shortlabel || node.label;
      const visibleWindowSequence = openWindowSequenceNodes.includes(node.id)
        ? node.windowSequence || []
        : [];

      const width = calculateNodeWidth(
        labelContent,
        true,
        visibleWindowSequence,
      );
      const height = calculateNodeHeight(
        labelContent,
        visibleWindowSequence,
      );

      if (node.label === "Start" || node.label === "End") {
        g.setNode(node.id, {
          label: labelContent,
          width: calculateNodeWidth(labelContent, false),
          height: calculateNodeHeight(labelContent),
          style: node.color ? `fill: ${node.color}` : "",
        });
      } else {
        g.setNode(node.id, {
          label: labelContent,
          width: width + 13,
          height: height,
          style: node.color ? `fill: ${node.color}` : "",
        });
        if (node.parentNode) {
          g.setParent(node.id, node.parentNode);
        } else {
          g.setParent(node.id, "mainprocess_cluster");
        }
      }
    });

    // Make sure orphan nodes are included
    relevantNodes.forEach((nodeId) => {
      if (!g.hasNode(nodeId)) {
        g.setNode(nodeId, { label: "" });
      }
    });

    // Add all filtered edges, WITHOUT additional filtering by frequency
    edges.forEach((edge) => {
      if (!g.edge(edge.from, edge.to)) {
        const edgePercentage = (edge.value / executionFrequency) * 100;
        g.setEdge(edge.from, edge.to, {
          label: `${
            edge.value / executionFrequency > 1
              ? "> 100%"
              : edgePercentage.toFixed(2)
          }`,
          curve: d3.curveMonotoneY,
          minlen: 1,
          weight: 200,
        });
      }
    });

    dagre.layout(g);

    // Position nodes
    const positionedNodes = nodes.map((node) => {
      const dagreNode = g.node(node.id);

      return {
        ...node,
        position: {
          x:
            dagreNode.x - (node.id === "Start" || node.id === "End" ? 52 : 200),
          y: dagreNode.y - dagreNode.height / 2,
        },
      };
    });

    // Group positions
    const parentNodePositions = [];
    g.nodes().forEach((nodeId) => {
      const node = g.node(nodeId);
      if (g.parent(nodeId) && g.parent(nodeId) !== "mainprocess_cluster") {
        const parentNodeId = g.parent(nodeId);
        const parentNode = g.node(parentNodeId);
        if (parentNode) {
          parentNodePositions[parentNodeId] = {
            x: parentNode.x,
            y: parentNode.y,
            width: parentNode.width,
            height: parentNode.height,
            label: parentNode.label,
          };
        }
      }
    });

    // Create group nodes
    const parentNodes = Array.from(relevantNodes)
      .filter((nodeId) => !nodes.find((node) => node.id === nodeId))
      .map((nodeId) => {
        const dagreNode = g.node(nodeId);
        const parentPos = parentNodePositions[nodeId];
        if (parentPos) {
          const { width, height, x, y, label } = parentPos;
          const childNode = nodes.find((node) => node.parentNode === nodeId);
          const color = childNode ? childNode.color : "";
          return {
            id: nodeId,
            label,
            position: { x: x - (width - 50) / 2, y: y - height / 2 },
            type: "group",
            width: width - 50,
            height,
            color,
            data: { label },
          };
        } else if (dagreNode) {
          return {
            id: nodeId,
            label: "",
            position: { x: dagreNode.x - 125, y: dagreNode.y - 81 },
            type: "group",
          };
        }
        return null;
      })
      .filter((node) => node !== null);

    const allPositionedNodes = [...parentNodes, ...positionedNodes];

    // Position edges
    const positionedEdges = g.edges().map((e) => {
      const edge = g.edge(e);
      return {
        id: `${e.v}-${e.w}`,
        source: e.v,
        target: e.w,
        points: edge.points,
        label: edge.label,
      };
    });

    // Adjust child node positions relative to their parents
    const finalPositionedNodes = allPositionedNodes.map((node) => {
      if (node.type === "group") return node;
      const parentNode = parentNodes.find(
        (parent) => parent.id === node.parentNode,
      );
      if (parentNode) {
        return {
          ...node,
          position: {
            x: node.position.x - parentNode.position.x,
            y: node.position.y - parentNode.position.y,
          },
        };
      }
      return node;
    });

    return {
      positionedNodes: finalPositionedNodes,
      edges: positionedEdges,
    };
  }

  function removeNodeFromActivity() {
    if (!selectedNodes.includes(selectedNode)) {
      selectedNodes = [...selectedNodes, selectedNode];
    }
    fetchWithSession(`/api/analysis/removeActivityMappingUIElement`, {
      method: "POST",
      body: JSON.stringify({
        unifiedlabels: selectedNodes,
        analysisid: analysisid,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        triggerUpdate = true;
        selectedNodes = [];
      });
  }

  function addNodesToActivity() {
    subprocessMenuOpen = true;
  }

  function expandActivity() {
    expandedNodes.push(selectedCluster);
    expandedNodes = expandedNodes;
    contextMenuOpen = false;
  }

  function collapseActivity(label) {
    expandedNodes = expandedNodes.filter((item) => item !== label);
  }

  function toggleWindowSequence(nodeId) {
    openWindowSequenceNodes = openWindowSequenceNodes.includes(nodeId)
      ? openWindowSequenceNodes.filter((item) => item !== nodeId)
      : [...openWindowSequenceNodes, nodeId];
  }

  function onNodeClick(event) {
    const id = event.detail.node.id;
    if (!id.match(/^(Start|End)( \d+)?$/gm)) selectedNode = id;
    if (selectedNodes.includes(id)) {
      selectedNodes = selectedNodes.filter((item) => item !== id);
      edgesData
        .filter((edge) => edge.from === id || edge.to === id)
        .forEach((edge) => {
          const edgeSelector = `[data-id="${edge.from}-${edge.to}"]`;
          const edgeElement = document.querySelector(edgeSelector);
          if (!edgeElement) return;
          const pathElement = edgeElement.querySelector("path");
          if (pathElement) {
            pathElement.classList.remove("edge_selected");
            let markerId = pathElement.getAttribute("marker-end");
            markerId = markerId.replace("url('#", "").replace("')", "");

            let markedEnd = document.getElementById(markerId);
            if (markedEnd) {
              const polyline = markedEnd.querySelector("polyline");
              if (polyline) {
                polyline.setAttribute("stroke", "black");
                polyline.setAttribute("fill", "black");
              }
            }
          }
        });
    } else {
      selectedNodes = [...selectedNodes, id];
      edgesData
        .filter((edge) => edge.from === id || edge.to === id)
        .forEach((edge) => {
          const edgeSelector = `[data-id="${edge.from}-${edge.to}"]`;
          const edgeElement = document.querySelector(edgeSelector);
          if (!edgeElement) return;
          const pathElement = edgeElement.querySelector("path");
          if (pathElement) {
            pathElement.classList.add("edge_selected");
            let markerId = pathElement.getAttribute("marker-end");
            markerId = markerId.replace("url('#", "").replace("')", "");

            let markedEnd = document.getElementById(markerId);
            if (markedEnd) {
              const polyline = markedEnd.querySelector("polyline");
              if (polyline) {
                polyline.setAttribute("stroke", "#5c25fa");
                polyline.setAttribute("fill", "#5c25fa");
              }
            }
          }
        });
    }
    selectedNodes = selectedNodes;
  }

  var mouseleave = function (id) {
    edgesData
      .filter((edge) => edge.from === id || edge.to === id)
      .forEach((edge) => {
        const edgeSelector = `[data-id="${edge.from}-${edge.to}"]`;
        const edgeElement = document.querySelector(edgeSelector);
        if (!edgeElement) return;
        const pathElement = edgeElement.querySelector("path");
        if (pathElement) {
          pathElement.classList.remove("edge_hovered");
          if (selectedNodes.includes(id)) {
            pathElement.classList.add("edge_selected");
          } else {
            pathElement.classList.remove("edge_selected");
          }
          let markerId = pathElement.getAttribute("marker-end");
          markerId = markerId.replace("url('#", "").replace("')", "");

          let markedEnd = document.getElementById(markerId);
          if (markedEnd) {
            const polyline = markedEnd.querySelector("polyline");
            if (polyline) {
              if (selectedNodes.includes(id)) {
                polyline.setAttribute("stroke", "#5c25fa");
                polyline.setAttribute("fill", "#5c25fa");
              } else {
                polyline.setAttribute("stroke", "black");
                polyline.setAttribute("fill", "black");
              }
            }
          }
        }

        const nextNode = document.querySelector(`[data-node-id="${edge.to}"]`);
        const prevNode = document.querySelector(
          `[data-node-id="${edge.from}"]`,
        );
        if (nextNode) nextNode.classList.remove("node_hovered");
        if (prevNode) prevNode.classList.remove("node_hovered");
      });
  };

  function mouseover(evt, id) {
    document
      .querySelectorAll(".node_hovered")
      .forEach((el) => el.classList.remove("node_hovered"));
    document
      .querySelectorAll(".edge_hovered")
      .forEach((el) => el.classList.remove("edge_hovered"));

    edgesData
      .filter((edge) => edge.from === id || edge.to === id)
      .forEach((edge) => {
        const edgeSelector = `[data-id="${edge.from}-${edge.to}"]`;
        const edgeElement = document.querySelector(edgeSelector);
        if (!edgeElement) return;
        const pathElement = edgeElement.querySelector("path");
        if (pathElement) {
          pathElement.classList.add("edge_hovered");
          let markerId = pathElement.getAttribute("marker-end");
          markerId = markerId.replace("url('#", "").replace("')", "");

          let markedEnd = document.getElementById(markerId);
          if (markedEnd) {
            const polyline = markedEnd.querySelector("polyline");
            if (polyline) {
              polyline.setAttribute("stroke", "#ff5f1f");
              polyline.setAttribute("fill", "#ff5f1f");
            }
          }
        }
        const nextNode = document.querySelector(`[data-node-id="${edge.to}"]`);
        const prevNode = document.querySelector(
          `[data-node-id="${edge.from}"]`,
        );
        if (nextNode) nextNode.classList.add("node_hovered");
        if (prevNode) prevNode.classList.add("node_hovered");
      });

    const currentNode = document.querySelector(`[data-node-id="${id}"]`);
    if (currentNode && !id.includes("_cluster"))
      currentNode.classList.add("node_hovered");
  }

  function closeContextMenu() {
    contextMenuOpen = false;
  }

  function hexToRgba(hex, alpha) {
    hex = hex.replace(/^#/, "");

    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((hexChar) => hexChar + hexChar)
        .join("");
    }

    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  function parentMouseover(event, id) {
    const targetDiv = document.querySelector(
      `div[data-id="${event.detail.node.id}"]`,
    );
    const contentDiv = targetDiv.querySelector(
      `div[data-node-id="${event.detail.node.id}"]`,
    );
    targetDiv.style.pointerEvents = "none";
    contentDiv.querySelectorAll("*").forEach((element) => {
      element.style.pointerEvents = "auto";
    });
  }

  function resetData() {
    return new Promise((resolve) => {
      nodes.set([]);
      edges.set([]);
      //resolve();
      setTimeout(resolve, 0);
    });
  }

  async function setData() {
    if (nodesData.length === 0 || edgesData.length === 0) return;
    const { positionedNodes, edges: positionedEdges } = calculateNodePositions(
      nodesData,
      edgesData,
      fullNodeInfo,
      minFrequency,
      nvariants,
    );

    isLoadingGraph = true;
    const nodeIds = new Set(positionedNodes.map((node) => node.id));

    await tick();

    nodes.set(
      positionedNodes.map((node) => {
        const isParentNode = node.id.endsWith("_cluster");
        const parentNodeExists =
          node.parentNode && nodeIds.has(node.parentNode);
        let posX = node.position.x + nvariants / 10;
        let posY = node.position.y;
        let finalPos = { x: posX, y: posY };

        return {
          id: node.id,
          data: {
            label: node.label,
            icon: node.image,
            isStart: node.label.match(/^Start( \d+)?$/gm) ? true : false,
            isEnd: node.label.match(/^End( \d+)?$/gm) ? true : false,
            color: node.color ? node.color : "#f4f4f4",
            handlemodal: openNodeMenu,
            isSelected: selectedNodes.includes(node.id),
            position: finalPos,
            handleCollapse: collapseActivity,
            handleWindowSequenceToggle: toggleWindowSequence,
            windowSequence: openWindowSequenceNodes.includes(node.id)
              ? node.windowSequence || []
              : [],
            hasWindowSequence: Boolean((node.windowSequence || []).length),
            windowSequenceOpen: openWindowSequenceNodes.includes(node.id),
          },
          position: finalPos,
          type: isParentNode ? "group" : "custom",
          dragging: false,
          selectedNode: selectedNode,
          parentId: parentNodeExists ? node.parentNode : "",
          style: isParentNode
            ? `background-color: ${hexToRgba(node.color, 0.5)}; width:${node.width}px; height:${node.height}px; border-radius: 8px; border: 10px solid ${hexToRgba(node.color, 0.2)};`
            : "",
        };
      }),
    );

    edges.set(
      positionedEdges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: "customEdg",
        markerEnd: {
          data_id: edge.id + "_marker",
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 30,
          color: "black",
        },
        data: {
          points: edge.points,
          label: edge.label,
        },
      })),
    );

    $nodes = $nodes;
    isLoadingGraph = false;
  }

  function extractClassStyles(classNames) {
    let cssText = "";
    const styleSheets = Array.from(document.styleSheets);

    styleSheets.forEach((sheet) => {
      try {
        const rules = Array.from(sheet.cssRules || sheet.rules);
        rules.forEach((rule) => {
          if (rule.type === CSSRule.STYLE_RULE && rule.selectorText) {
            // Check exact match with target classes
            const matchesTarget = classNames.some((className) => {
              // Check if the selector is the class or contains it as a whole class
              return (
                rule.selectorText === className ||
                rule.selectorText.startsWith(`${className}.svelte`)
              );
            });

            if (matchesTarget) {
              cssText += rule.cssText + "\n";
            }
          }
        });
      } catch (e) {
        // Ignore CORS errors
      }
    });

    return cssText;
  }

  onMount(async () => {
    if (!isLoadingGraph) {
      await resetData().then(() => setData());
    }
  });

  $$restProps;
</script>

<!-- Kontextmenue Knoten -->
<WorkflowExplorerDropdownMenu
  bind:visible={contextMenuOpen}
  {selectedNode}
  parentNode={currentParentNode}
  position={menuPosition}
  {analysisid}
  addActitvityClicked={addNodesToActivity}
  removeActitvityClicked={removeNodeFromActivity}
  expandActitvityClicked={expandActivity}
  {isActivityNode}
  {AIExplanation}
/>

<div
  class="svelte-flow-container"
  style="width:{width}; height:{height};  display:grid;"
>
  <div style="grid-row: 1; grid-column:1; height:100%; padding-bottom: 50px;">
    <SvelteFlow
      {nodes}
      {edges}
      {nodeTypes}
      {edgeTypes}
      fitView
      minZoom={0.05}
      nodesDraggable={false}
      connectionMode={ConnectionMode.Loose}
      on:nodeclick={onNodeClick}
      on:openContextMenu={(event) => {
        openNodeMenu(event, event.detail.node.id);
      }}
      on:paneclick={closeContextMenu}
      on:nodemouseenter={(event) => {
        if (!event.detail.node.id.endsWith("_cluster"))
          mouseover(event, event.detail.node.id);
        else {
          parentMouseover(event, event.detail.node.id);
        }
      }}
      on:nodemouseleave={(event) => mouseleave(event.detail.node.id)}
      on:window:click={() => {
        contextMenuOpen = false;
      }}
    >
      <!-- <Background
        variant={BackgroundVariant.Dots}
        gap="25"
        size="2"
        patternColor="#919396"
      /> -->
      {#if seeMinimap}
        <MiniMap zoomable pannable height={120} />
      {/if}
    </SvelteFlow>
  </div>
</div>

<GraphGrouper
  bind:selectedNode
  bind:selectedNodes
  bind:subprocessMenuOpen
  slot="nodeMenu"
  bind:triggerUpdate
/>

<style>
  :global(.svelteflow-controls-button-fitView) {
    cursor: pointer;
    display: flex;
    height: 3rem;
    width: 3rem;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    background-color: var(--pax-black);
    color: var(--pax-white);
    transition: 300ms ease-in-out;
  }

  :global(.clusterlabel) {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  :global(.more_filled_icon) {
    cursor: pointer;
  }

  :global(.collapseicon) {
    cursor: pointer;
    background-color: white;
    border-radius: 50%;
    border: 1px solid black;
  }
  :global(.collapseicon:hover) {
    background-color: #2a2a2f;
  }
  :global(g.node) {
    cursor: pointer;
  }

  :global(div.nodelabel) {
    display: flex !important;
    gap: 0.5rem;
    align-items: center;
    padding: 0.25rem 0.5rem;
  }

  :global(.svelte-flow__node .node_hovered) {
    border: 2px solid #ff5f1f !important;
  }

  :global(.svelte-flow__node .node_hovered) {
    stroke: #ff5f1f !important;
  }

  :global(.svelte-flow__edge .edge_hovered) {
    stroke: #ff5f1f !important;
  }

  :global(.svelte-flow__edge .edge_hovered defs path) {
    stroke: #ff5f1f !important;
  }

  :global(.svelte-flow__edge .edge_selected) {
    stroke: #5c25fa !important;
  }

  :global(.svelte-flow__edge .edge_selected defs path) {
    stroke: #5c25fa !important;
  }

  :global(.edge_selected) {
    stroke: #5c25fa !important;
  }

  :global(.node_selected) {
    stroke: #5c25fa !important;
  }

  :global(foreignObject) {
    overflow: visible !important;
  }

  :global(.svelte-flow__attribution) {
    background-color: transparent !important;
  }
  :global(.svelte-flow__attribution a) {
    background-color: transparent !important;
    color: transparent !important;
  }
</style>
